import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  login as loginAPI,
  logout as logoutAPI,
  getUserProfile,
} from "../api/auth";
import { getSetupStatus } from "../api/airwallex";
import SecureSS from "../utils/SecureStorage";
import { extractErrorMessage, AUTH_ERRORS } from "../utils/errorUtils";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth state from session storage, with SSO token support
  useEffect(() => {
    const initAuth = async () => {
      // Check for SSO token from Admin UI
      const urlParams = new URLSearchParams(window.location.search);
      const ssoToken = urlParams.get("sso_token");

      if (ssoToken) {
        // Strip the token from the URL (address bar + history entry)
        // SYNCHRONOUSLY, before any network call. Otherwise the bearer token
        // lingers in document.location, the history entry, and the Referer
        // header of any request made before cleanup.
        window.history.replaceState({}, document.title, "/");

        try {
          // Store the token
          SecureSS.setToken(ssoToken);

          // Validate token by fetching user profile
          const profileResponse = await getUserProfile();

          if (!profileResponse?.body) {
            throw new Error("Invalid token or user profile not found");
          }

          const userProfile = profileResponse.body;

          // Validate user type and role — same rules as normal login
          const allowedRoles = ["CLIENT_ADMIN", "CLIENT_FINANCE", "CLIENT_COMMERCIAL_TEAM"];
          const isValidUser =
            userProfile.userType === "CLIENT" && allowedRoles.includes(userProfile.role);

          if (!isValidUser) {
            throw new Error("You do not have permission to access Wallet");
          }

          // Store user data
          SecureSS.setUserProfile(userProfile);
          SecureSS.setAuthenticated(true);
          SecureSS.setUserRole(userProfile.role);
          SecureSS.setHomePage(userProfile.homePage || "/");

          // Update state
          setUser(userProfile);
          setAuthenticated(true);

          // (Token was already stripped from the URL synchronously above.)

          // Determine initial route (same logic as normal login)
          try {
            const accountResponse = await getSetupStatus();

            if (accountResponse.status === 204) {
              navigate("/wallet/setup");
            } else if (accountResponse.status === 200 && accountResponse.data) {
              const { status } = accountResponse.data;
              navigate(status === "ACTIVE" ? "/" : "/wallet/setup");
            } else {
              navigate("/wallet/setup");
            }
          } catch {
            navigate("/wallet/setup");
          }
        } catch (error) {
          console.error("SSO login failed:", error);
          SecureSS.clear();
          setAuthenticated(false);
          setUser(null);
          toast.error(error.message || "SSO login failed. Please login manually.");
          navigate("/login");
        } finally {
          setLoading(false);
        }
        return;
      }

      // Normal session restoration from storage
      const isAuth = SecureSS.isAuthenticated();
      const storedUser = SecureSS.getUserProfile();

      if (isAuth && storedUser) {
        setAuthenticated(true);
        setUser(storedUser);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Listen for session expiration events from axios interceptor
  useEffect(() => {
    const handleSessionExpired = (event) => {
      // Clear state
      setAuthenticated(false);
      setUser(null);

      // Navigate to login
      navigate("/login");
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, [navigate]);

  const login = async (credentials) => {
    try {
      setLoading(true);

      // Call login API
      const response = await loginAPI(credentials);

      // Check if response exists
      if (!response || !response.body) {
        throw new Error(AUTH_ERRORS.INVALID_RESPONSE);
      }

      // Check if login failed (server returns 200 but with error message)
      if (response.body.responseText || response.body.error) {
        const errorMessage = extractErrorMessage(response.body);
        throw new Error(errorMessage);
      }

      // Check if authentication data exists
      if (!response.body.authentication || !response.body.authentication.accessToken) {
        throw new Error(AUTH_ERRORS.MISSING_AUTH_DATA);
      }

      const token = response.body.authentication.accessToken;

      // Store token
      SecureSS.setToken(token);

      // Fetch user profile
      const profileResponse = await getUserProfile();

      if (profileResponse?.body) {
        const userProfile = profileResponse.body;

          // Validate user credentials - only CLIENT users with allowed roles
          const allowedRoles = ["CLIENT_ADMIN", "CLIENT_FINANCE", "CLIENT_COMMERCIAL_TEAM"];
          const isValidUser = userProfile.userType === "CLIENT" && allowedRoles.includes(userProfile.role);

          if (!isValidUser) {
            // Clear any stored auth data
            SecureSS.clear();
            setAuthenticated(false);
            setUser(null);

            // Return error without storing user data
            throw new Error(AUTH_ERRORS.INVALID_CREDENTIALS);
          }

          // Store user data only for valid users
          SecureSS.setUserProfile(userProfile);
          SecureSS.setAuthenticated(true);
          SecureSS.setUserRole(userProfile.role);
          SecureSS.setHomePage(userProfile.homePage || "/");

          // Update state
          setUser(userProfile);
          setAuthenticated(true);

          toast.success("Login successful!");

          // Fetch account details to determine initial route
          try {
            const accountResponse = await getSetupStatus();

            // Check for saved redirect path
            const redirectPath = localStorage.getItem("postLoginRedirect");
            if (redirectPath) {
              localStorage.removeItem("postLoginRedirect");
              navigate(redirectPath);
              return { success: true };
            }

            // Handle HTTP 204 No Content - no account exists yet
            if (accountResponse.status === 204) {
              navigate("/wallet/setup");
              return { success: true };
            }

            // Handle HTTP 200 with data
            if (accountResponse.status === 200 && accountResponse.data) {
              const { status } = accountResponse.data;

              // If account is ACTIVE, go to dashboard; otherwise go to setup
              if (status === "ACTIVE") {
                navigate("/");
              } else if (status) {
                // Account exists but not active (CREATED, SUBMITTED, etc.)
                navigate("/wallet/setup");
              } else {
                // No status field, treat as no account
                navigate("/wallet/setup");
              }
            } else {
              // If account details not available, go to setup
              navigate("/wallet/setup");
            }

            return { success: true };
          } catch (error) {
            console.error("Failed to fetch account details:", error);
            // Fallback to setup page if account check fails
            navigate("/wallet/setup");
            return { success: true };
          }
        } else {
          throw new Error(AUTH_ERRORS.PROFILE_FETCH_FAILED);
        }
    } catch (error) {
      console.error("Login error:", error);

      // Clear any stored auth data on error
      SecureSS.clear();
      setAuthenticated(false);
      setUser(null);

      toast.error(error.message || "Login failed. Please try again.");
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async (options = {}) => {
    const { showSuccessMessage = true } = options;

    try {
      setLoading(true);

      // Call logout API
      await logoutAPI();

      // Clear session storage
      SecureSS.clear();

      // Update state
      setAuthenticated(false);
      setUser(null);

      // Redirect to login
      navigate("/login");

      // Only show success message if explicitly requested (for manual logout)
      // Skip for automatic logout due to authorization failure
      if (showSuccessMessage) {
        toast.success("Logged out successfully");
      }
    } catch (error) {
      console.error("Logout error:", error);

      // Clear session even if API call fails
      SecureSS.clear();
      setAuthenticated(false);
      setUser(null);
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const value = {
    authenticated,
    user,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
